import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Fade from "@mui/material/Fade";
import CircularProgress from "@mui/material/CircularProgress";
import { generateRecommendedPrompts } from "../utils/AwsCalls";

function DynamicRecommendedPrompts({ 
  answer, 
  userQuery, 
  onPromptClick, 
  answerIndex 
}) {
  const { t } = useTranslation();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const hasGeneratedRef = useRef(false);
  const lastAnswerTextRef = useRef("");

  useEffect(() => {
    const generatePrompts = async () => {
      // Only generate prompts if we have a text answer and it's not empty
      // and we haven't already generated prompts for this specific answer text
      if (!answer?.text || 
          answer.text.trim().length === 0 || 
          (hasGeneratedRef.current && lastAnswerTextRef.current === answer.text)) {
        return;
      }

      setLoading(true);
      setError(false);
      hasGeneratedRef.current = true;
      lastAnswerTextRef.current = answer.text;
      
      try {
        const generatedPrompts = await generateRecommendedPrompts(answer, userQuery);
        setPrompts(generatedPrompts);
      } catch (err) {
        console.error("Error generating dynamic prompts:", err);
        // Check if it's a throttling error
        if (err.name === 'ThrottlingException' || err.message?.includes('Too many requests')) {
          console.warn('Bedrock throttling detected, skipping dynamic prompts');
          setError(false); // Don't show error for throttling, just skip
          setPrompts([]); // Clear prompts
        } else {
          setError(true);
        }
      } finally {
        setLoading(false);
      }
    };

    // Reset when answer changes
    if (lastAnswerTextRef.current !== answer?.text) {
      hasGeneratedRef.current = false;
      setPrompts([]);
      setError(false);
    }

    // Add a small delay to ensure the answer is fully rendered
    const timer = setTimeout(generatePrompts, 500);
    return () => clearTimeout(timer);
  }, [answer, userQuery]);

  // Don't render anything if no answer text or if there are no prompts and not loading
  if (!answer?.text || (!loading && prompts.length === 0 && !error)) {
    return null;
  }

  return (
    <Fade in={!loading && prompts.length > 0} timeout={800}>
      <Box sx={{ mt: 2, mb: 1 }}>
        {loading && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 2 
          }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: '0.85rem' }}
            >
              {t('chat.generatingPrompts')}
            </Typography>
          </Box>
        )}
        
        {!loading && prompts.length > 0 && (
          <>
            <Typography
              variant="subtitle2"
              color="primary"
              sx={{ 
                fontSize: "0.8rem", 
                fontWeight: 500, 
                mb: 1,
                opacity: 0.8
              }}
            >
              {t('chat.followUpQuestions')}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              flexWrap: 'wrap'
            }}>
              {prompts.map((prompt, index) => (
                <Chip
                  key={`${answerIndex}-prompt-${index}`}
                  label={prompt}
                  onClick={() => onPromptClick(prompt)}
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    lineHeight: 1.2,
                    cursor: 'pointer',
                    backgroundColor: 'rgba(0, 0, 255, 0.08)',
                    color: 'primary.main',
                    border: '1px solid',
                    borderColor: 'rgba(0, 0, 255, 0.2)',
                    borderRadius: '16px',
                    height: 'auto',
                    padding: '4px 8px',
                    minHeight: '28px',
                    maxWidth: { xs: '100%', sm: '200px' },
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 8px rgba(4, 115, 100, 0.15)',
                    },
                    '& .MuiChip-label': {
                      padding: '0',
                      display: 'block',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word'
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                  clickable
                />
              ))}
            </Box>
          </>
        )}
        
        {error && (
          <Typography 
            variant="caption" 
            color="error"
            sx={{ fontSize: '0.75rem', opacity: 0.7 }}
          >
            {t('chat.promptGenerationError')}
          </Typography>
        )}
      </Box>
    </Fade>
  );
}

export default DynamicRecommendedPrompts;